import React from "react"

import { redirect, RouteObject } from "react-router-dom"

import { Bee } from "~/src/components/BeeKit"
import { DashboardStoreInventoryReorderModal } from "~/src/components/Dashboard/StoreInventoryReorderModal"
import { StoreItemInventoryTileProps } from "~/src/components/Dashboard/StoreItemInventoryTile"
import { DashboardStoreCollectionItemSettings } from "~/src/components/DashboardStores/DashboardStoreCollectionItemSettings"
import {
  DashboardStoreInventoryShow,
  DashboardStoreInventoryShowProps,
} from "~/src/components/DashboardStores/DashboardStoreInventoryShow"
import { ErrorPage } from "~/src/components/ErrorPage"
import { requestCartStore } from "~/src/hooks/useRequestCart"
import { appClient, AppClientError } from "~/src/lib/appClients"

export const basename = "/dashboard/stores"

const createRoutes = (): RouteObject[] => [
  {
    path: [basename, ":id/:itemType/:itemId/settings"].join("/"),
    element: <DashboardStoreCollectionItemSettings />,
    hydrateFallbackElement: <></>,
    errorElement: <ErrorPage />,
    async loader({ request }) {
      const url = new URL(request.url)

      const response = await appClient.get(url.pathname + ".json", {
        headers: { Accept: "application/json" },
        params: url.searchParams,
      })

      return response.data
    },

    async action({ request, params }) {
      const { id: storeId } = params
      const url = new URL(request.url)
      const formData = await request.formData()
      try {
        const response = await appClient.patch(url.pathname, formData, {
          headers: { Accept: "application/json" },
        })

        return redirect(`${basename}/${storeId}/inventory`)
      } catch (error) {
        if (error instanceof AppClientError && typeof error?.status === "number" && error.status < 500) {
          return error.response?.data
        }

        throw error
      }
    },
  },
  {
    path: `${basename}/:id/inventory`,
    element: <DashboardStoreInventoryShow />,
    hydrateFallbackElement: <></>,
    errorElement: <ErrorPage />,
    async loader({ request, params }): Promise<DashboardStoreInventoryShowProps> {
      const { id } = params
      const url = new URL(request.url)
      const response = await appClient.get(`${basename}/${id}/inventory`, {
        headers: { Accept: "application/json" },
        params: url.searchParams,
      })

      return response.data
    },
    shouldRevalidate: ({ currentUrl, nextUrl }) => {
      const current = new URL(currentUrl)
      const next = new URL(nextUrl)
      const isModalPath = (url: URL) => url.pathname.includes("/reorder")
      if (isModalPath(current) || isModalPath(next)) {
        return false // don't revalidate if it's a modal
      }

      const currentPath = current.pathname
      const nextPath = next.pathname
      // revalidate if the path changes (pagination, ...)
      if (currentPath !== nextPath) {
        return true
      }

      const searchChanged = current.search !== next.search
      const sortChanged = current.searchParams.get("sort") !== next.searchParams.get("sort")
      // revalidate if the search or sort params change (search, sort, ...)
      return searchChanged || sortChanged
    },
    children: [
      {
        path: `:itemType/:itemId/reorder`,
        element: <DashboardStoreInventoryReorderModal />,
        shouldRevalidate: ({ formAction }) => {
          return formAction?.includes("reorder") || false
        },
        async loader({ request }): Promise<StoreItemInventoryTileProps> {
          const url = new URL(request.url)

          const response = await appClient.get(`${url.pathname}.json`, {
            headers: { Accept: "application/json" },
          })

          return response.data
        },
        async action({ request, params }) {
          const url = new URL(request.url)
          const formData = await request.formData()
          const { id: storeId, itemType } = params

          if (itemType === "store_kit") {
            Bee.toast.error({ message: "Reordering store kits is not supported yet." })
            return { errors: ["Reordering store kits is not supported yet."] }
          }

          try {
            await appClient.post(url.pathname, formData, {
              headers: { Accept: "application/json" },
            })

            requestCartStore.getState().itemChanged()

            return redirect(`${basename}/${storeId}/inventory`)
          } catch (error) {
            const errors = error.response?.data?.errors || ["There was an error adding items. Please try again."]
            errors.forEach((error: any) => {
              if (typeof error === "string") {
                Bee.toast.error({ message: error })
              } else if (error?.message) {
                Bee.toast.error({ message: error.message })
              }
            })
            return { errors }
          }
        },
      },
    ],
  },
]

export default createRoutes
